import { ConfirmModal } from '@src/components/common/ConfirmModal';
import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { ToggleGroup } from '@src/components/common/ToggleGroup';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CircleCheckIcon from '@src/svgs/CircleCheckIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import DrawFreehandIcon from '@src/svgs/DrawFreehandIcon';
import EmojiIcon from '@src/svgs/EmojiIcon';
import EmptyCommentsIllustration from '@src/svgs/EmptyCommentsIllustration';
import PencilIcon from '@src/svgs/PencilIcon';
import {
  decodeMentionTokens,
  encodeMentionTokens,
  splitBodyForRender,
} from '@src/utility/commentMentions';
import {
  formatAbsoluteTime,
  formatRelativeTimeShort,
} from '@src/utility/relativeTime';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import {
  DEFAULT_FRAMERATE,
  formatTimecode,
  parseTimecode,
  snapToFrame,
} from '@src/utility/timecode';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../../common/tooltipStyles';
import { CommentTimecodeField } from './CommentTimecodeField';
import { EMOJI_PICKER_MENU_WIDTH, EmojiPickerMenu } from './EmojiPickerMenu';
import { MentionSuggestions } from './MentionSuggestions';
import { useMentionAutocomplete } from './useMentionAutocomplete';

import type { MentionPerson } from './useMentionAutocomplete';
import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type { CommentStore } from '@src/stores-v2/CommentStore';
import type { Comment } from '@src/types.ts/comment';

interface CommentListProps {
  store: CommentStore;
  playback: PlaybackBridge;
  /** Frame rate for the edit-mode timecode field; falls back to 30fps. */
  framerate?: number;
  /** Workspace teammates offered by the @-mention autocomplete. */
  mentionPeople?: MentionPerson[];
  /**
   * Collision-free avatar colors by person name, built once by the owning modal
   * so every comment surface (this list, the markers, the mention popovers)
   * agrees on a hue. Authors outside the map fall back to the label hash.
   */
  personColors?: Map<string, string>;
  /**
   * Id of the comment whose drawing is currently painted on the frame, or
   * `null` for none (PLAT-11291). Owned by the modal, because the marks render
   * over the player rather than in this list.
   */
  selectedDrawingId?: string | null;
  /** Ask the host to paint this comment's marks, or `null` to clear them. */
  onSelectDrawing?: (comment: Comment | null) => void;
}

/**
 * Status view the toggle group can switch to. Single-select (a thread is
 * exactly one of open/resolved, so the views are mutually exclusive); defaults
 * to Open — the actionable set.
 */
type StatusFilter = 'open' | 'resolved' | 'all';

/**
 * Thread list for the Content Studio clip-preview Comments tab. Chat-style:
 * top-level comments render oldest-first (newest at the bottom, nearest the
 * composer), each with author, relative time, body, and a timecode chip that
 * seeks the player; replies nest beneath (also oldest-first). Includes an inline
 * edit affordance for your own comments and a "Reply" composer per thread
 * (PLAT-10302 list, PLAT-10303 replies).
 *
 * The model is two-level: a reply's `parentId` points at the top-level comment,
 * so replies flatten to a single level of nesting — a reply to any message in a
 * thread attaches to that thread's top-level comment.
 */
export const CommentList: React.FC<CommentListProps> = observer(
  ({
    store,
    playback,
    mentionPeople = [],
    personColors,
    selectedDrawingId,
    onSelectDrawing,
    framerate = DEFAULT_FRAMERATE,
  }) => {
    const { threads } = store;

    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    /**
     * Timecode being edited alongside the body, as raw text so a half-typed
     * value survives a keystroke. `null` means the comment is unpinned — a
     * general comment with no moment attached.
     */
    const [timeDraft, setTimeDraft] = useState<string | null>(null);
    // People referenced by the body being edited (from decode at startEdit),
    // so saveEdit can re-tokenize them even off-roster.
    const editMentionsRef = useRef<{ id: string; name: string }[]>([]);

    // @-mention autocomplete for the (single) open edit box — same contract as
    // the reply composer, so an edit can add a mention rather than only keep
    // the ones the body already had.
    const editTextareaRef = useRef<HTMLTextAreaElement>(null);
    const editMention = useMentionAutocomplete(
      draft,
      setDraft,
      editTextareaRef,
      mentionPeople,
    );

    // Top-level comment id whose reply composer is open (one open at a time).
    const [replyParentId, setReplyParentId] = useState<string | null>(null);
    const [replyDraft, setReplyDraft] = useState('');
    const [replyPosting, setReplyPosting] = useState(false);

    // @-mention autocomplete for the (single) open reply composer.
    const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
    const replyMention = useMentionAutocomplete(
      replyDraft,
      setReplyDraft,
      replyTextareaRef,
      mentionPeople,
    );

    // Comment awaiting delete confirmation; `isTop` tailors the modal copy
    // (deleting a top-level comment removes its whole thread).
    const [pendingDelete, setPendingDelete] = useState<{
      id: string;
      isTop: boolean;
    } | null>(null);

    // Status view (toggle group). Defaults to Open — the actionable set;
    // resolved threads stay one click away under Resolved / All.
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');

    // Per-comment quick-reactions popover (one open at a time). The trigger ref
    // is reassigned to whichever comment's add-reaction button was clicked, so a
    // single anchored dropdown serves every card.
    const [reactionPickerId, setReactionPickerId] = useState<string | null>(
      null,
    );
    const reactionTriggerRef = useRef<HTMLButtonElement | null>(null);
    const { menuRef: reactionMenuRef, style: reactionMenuStyle } =
      useAnchoredDropdown(
        reactionPickerId !== null,
        reactionTriggerRef,
        () => setReactionPickerId(null),
        260,
        { width: EMOJI_PICKER_MENU_WIDTH },
      );

    // Chat-style: keep the newest comment (rendered at the bottom) in view —
    // scroll to the bottom on first load and whenever a new thread appears (so
    // the comment you just posted is immediately visible).
    const listRef = useRef<HTMLDivElement>(null);
    const prevThreadCount = useRef(0);

    // Edge-fade visibility as a pure scroll affordance: each fade shows only when
    // there's hidden content in that direction. Scrolled fully to the bottom (or
    // top) → that fade disappears, so it never dims the last/first comment at rest.
    const [fadeEdges, setFadeEdges] = useState({
      hasAbove: false,
      hasBelow: false,
    });
    const syncFadeEdges = useCallback(() => {
      const el = listRef.current;
      if (!el) return;
      const hasAbove = el.scrollTop > 1;
      const hasBelow = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setFadeEdges(prev =>
        prev.hasAbove === hasAbove && prev.hasBelow === hasBelow
          ? prev
          : { hasAbove, hasBelow },
      );
    }, []);

    useEffect(() => {
      const el = listRef.current;
      // A new thread (the one you just posted, or one that arrived) is appended
      // at the bottom — scroll straight to it so the newest comment is visible.
      if (el && threads.length > prevThreadCount.current) {
        el.scrollTop = el.scrollHeight;
      }
      prevThreadCount.current = threads.length;
    }, [threads.length]);

    // Re-sync the edge fades after every render. The rendered list height can
    // change with no scroll event — the Status filter toggling, a thread being
    // resolved out of the active filter, an edited comment growing taller —
    // which would otherwise leave a fade stuck until the next scroll.
    // `setFadeEdges` is a no-op when nothing changed, so this can't loop.
    useEffect(() => {
      syncFadeEdges();
    });

    // No comments at all: the filter bar would have nothing to act on, so we
    // keep the original bare empty state (and omit the list entirely). While
    // the initial fetch is in flight we don't yet know the list is empty, so
    // say we're loading instead. (Background polls never set `isLoading`.)
    if (threads.length === 0) {
      return (
        <EmptyState>
          {store.isLoading ? (
            'Loading comments…'
          ) : (
            <EmptyArt data-testid="comments-empty-art">
              <EmptyCommentsIllustration />
              <span>No comments yet.</span>
            </EmptyArt>
          )}
        </EmptyState>
      );
    }

    const filteredThreads = threads.filter(({ isResolved }) => {
      if (statusFilter === 'all') return true;
      return isResolved ? statusFilter === 'resolved' : statusFilter === 'open';
    });

    // Thread counts per view, shown on each toggle segment ("Open 3").
    const resolvedCount = threads.filter(({ isResolved }) => isResolved).length;
    const statusCounts: Record<StatusFilter, number> = {
      open: threads.length - resolvedCount,
      resolved: resolvedCount,
      all: threads.length,
    };

    // View-specific empty copy ("All" can't be empty while threads exist).
    const filterEmptyMessage =
      statusFilter === 'resolved'
        ? 'No resolved comments.'
        : 'No open comments.';

    // Absolute thread number ("#N") shown on each top-level comment. Keyed off
    // the store's oldest-first `threads` order, so #1 is the first comment and
    // the number stays stable regardless of the current filter or display order.
    const threadNumber = new Map(
      threads.map((t, i): [string, number] => [t.top.id, i + 1]),
    );

    const seekTo = (seconds: number) => {
      // Clamp to the loaded clip; fall back to the raw value before duration loads.
      const clamped = Math.max(
        0,
        Math.min(seconds, playback.duration || seconds),
      );
      playback.setTime(clamped);
    };

    /**
     * Paint a comment's marks on the frame they were drawn on: pause first (a
     * mark belongs to one frame, and ink over moving video is meaningless), then
     * seek, then hand the comment to the host to render. Clicking the same
     * comment again clears it — the way back out without hunting for a control.
     */
    const toggleDrawing = (comment: Comment) => {
      if (selectedDrawingId === comment.id) {
        onSelectDrawing?.(null);
        return;
      }
      playback.pause();
      if (comment.videoTimeSeconds !== null) seekTo(comment.videoTimeSeconds);
      onSelectDrawing?.(comment);
    };

    // ---- Edit ---------------------------------------------------------------

    const startEdit = (comment: Comment) => {
      setEditingId(comment.id);
      // Edit the friendly display form (`@Name`), never raw tokens. Remember
      // the people the tokens referenced so saving can restore them even if
      // they've since left the roster.
      const decoded = decodeMentionTokens(comment.body);
      editMentionsRef.current = decoded.people;
      // Picks belong to the draft that made them: a leftover pick from the
      // previous edit would silently re-point an identical @Name in this body.
      editMention.resetPicks();
      setDraft(decoded.text);
      setTimeDraft(
        comment.videoTimeSeconds === null
          ? null
          : formatTimecode(comment.videoTimeSeconds, framerate),
      );
    };

    const cancelEdit = () => {
      setEditingId(null);
      setDraft('');
      setTimeDraft(null);
    };

    /**
     * The timecode the edit form would commit: `null` for unpinned, a number
     * when the field parses inside the clip, `'invalid'` when it doesn't (which
     * blocks Save rather than silently discarding what was typed).
     */
    const editedTime = (): number | null | 'invalid' => {
      if (timeDraft === null) return null;
      const parsed = parseTimecode(timeDraft, framerate);
      if (parsed === null) return 'invalid';
      // Commit at frame resolution. The parser also accepts a bare seconds
      // value ("12.3456"), which would otherwise store an off-frame time while
      // every other path here — drag, the steppers, Use Playhead — is already
      // frame-aligned. Snap before the range check so a value that rounds up
      // past the end of the clip is still rejected.
      const snapped = snapToFrame(parsed, framerate);
      if (playback.duration > 0 && snapped > playback.duration) {
        return 'invalid';
      }
      return snapped;
    };

    const saveEdit = async (comment: Comment) => {
      const trimmed = draft.trim();
      if (!trimmed || isSaving) return;
      const nextTime = editedTime();
      if (nextTime === 'invalid') return;
      // Compare at frame resolution: the field renders a rounded timecode, so a
      // value the user never touched can differ from the stored float by less
      // than a frame and would otherwise look like an edit.
      const timeChanged =
        nextTime === null || comment.videoTimeSeconds === null
          ? nextTime !== comment.videoTimeSeconds
          : formatTimecode(nextTime, framerate) !==
            formatTimecode(comment.videoTimeSeconds, framerate);

      // Re-tokenize display mentions against the roster, plus whoever the
      // original body mentioned (kept from startEdit's decode), plus anyone
      // picked from the popover during this edit — later entries win.
      const encoded = encodeMentionTokens(trimmed, [
        ...mentionPeople,
        ...editMentionsRef.current,
        ...editMention.picks,
      ]);
      const bodyChanged = encoded !== comment.body;

      // Nothing changed — just leave edit mode without a needless write.
      if (!bodyChanged && !timeChanged) {
        cancelEdit();
        return;
      }
      setIsSaving(true);
      // Two independent writes: moving is not an edit (it must not stamp
      // `editedAt`), so they can't be folded into one mutation. Body first, so
      // a failure leaves the more visible change un-applied and retryable.
      //
      // Failure is tracked per call rather than by reading `store.lastError`
      // between them: the store only clears that flag when a mutation *starts*,
      // so an error left over from an earlier action (a failed reply, reaction,
      // delete…) would gate the second write on stale state — and a time-only
      // save, which runs no first write to refresh the flag, would do nothing
      // at all and never close the form.
      let failed = false;
      if (bodyChanged) {
        await store.editComment(comment.id, encoded);
        failed = !!store.lastError;
      }
      if (timeChanged && !failed) {
        await store.setCommentTime(comment.id, nextTime, 'timecode');
        failed = !!store.lastError;
      }
      setIsSaving(false);
      // Keep the draft open on error so the edit can be retried; the store
      // rolls back and surfaces `lastError`.
      if (!failed) cancelEdit();
    };

    const onEditKeyDown = (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
      comment: Comment,
    ) => {
      // Enter saves; Shift+Enter inserts a newline; Escape cancels.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void saveEdit(comment);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Cancel just the edit — don't let Escape bubble to the modal's
        // close handler.
        e.stopPropagation();
        cancelEdit();
      }
    };

    // ---- Reply --------------------------------------------------------------

    const openReply = (topId: string) => {
      setReplyParentId(topId);
      setReplyDraft('');
    };

    const cancelReply = () => {
      setReplyParentId(null);
      setReplyDraft('');
    };

    const postReply = async (topId: string) => {
      const trimmed = replyDraft.trim();
      if (!trimmed || replyPosting) return;
      setReplyPosting(true);
      // The reply editor holds display mentions (`@Name`); tokenize at post
      // time (same contract as the main composer) — picks last so a teammate
      // chosen from the popover wins over the roster's by-name guess.
      const picks = [...mentionPeople, ...replyMention.picks];
      // Close the composer immediately — the store inserts the reply
      // optimistically, so the reply would otherwise sit above a composer still
      // showing "Posting…" until the network write lands (the main composer
      // clears on the same reasoning, PLAT-12039).
      cancelReply();
      await store.reply(topId, encodeMentionTokens(trimmed, picks));
      setReplyPosting(false);
      // On failure the store rolls back the optimistic reply and surfaces
      // `lastError`; reopen the composer with the draft so it can be retried.
      if (store.lastError) {
        setReplyParentId(topId);
        setReplyDraft(trimmed);
      }
    };

    const onReplyKeyDown = (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
      topId: string,
    ) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void postReply(topId);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Cancel just the reply composer — don't let Escape bubble to the
        // modal's close handler.
        e.stopPropagation();
        cancelReply();
      }
    };

    // ---- Delete -------------------------------------------------------------

    // Soft-delete (owner-only), confirmed via a modal. Deleting a top-level
    // comment removes its whole thread; the store handles the optimistic
    // removal + cascade and rolls back on error.
    const confirmDelete = () => {
      if (!pendingDelete) return;
      void store.deleteComment(pendingDelete.id);
      setPendingDelete(null);
    };

    // ---- Render -------------------------------------------------------------

    // Renders a single comment (top-level or reply). `topId` is the thread's
    // top-level id a reply on this comment should attach to; `isReply` switches
    // the test id.
    const renderComment = (
      comment: Comment,
      topId: string,
      isReply: boolean,
      isResolved = false,
      threadNo?: number,
    ) => {
      const isEditing = editingId === comment.id;
      const isOwn = store.isOwnComment(comment);
      const { videoTimeSeconds } = comment;
      // Collision-free roster color first (the modal's shared map — two people
      // must never share a hue); hash fallback for authors outside the roster.
      // Keyed on display name: authorId is a Clerk id, unrelated to the roster
      // person id, so name is the shared identity across surfaces.
      const authorColor =
        personColors?.get(comment.authorName) ??
        getSpeakerColorFromLabel(comment.authorName || comment.authorId);
      // Full date(s) for the timestamp's hover tooltip; the visible label stays
      // compact ("3h").
      const timeTooltip =
        `Created ${formatAbsoluteTime(comment.createdAt)}` +
        (comment.editedAt
          ? ` · Edited ${formatAbsoluteTime(comment.editedAt)}`
          : '');
      // Emoji that currently have at least one reaction, for the pill row.
      const reactionEntries = Object.entries(comment.reactions).filter(
        ([, ids]) => ids.length > 0,
      );
      return (
        <Item
          key={comment.id}
          data-testid={isReply ? 'comment-reply-item' : 'comment-list-item'}
        >
          <AvatarCol>
            <SpeakerAvatar
              name={comment.authorName}
              color={authorColor}
              size={20}
              initialsColor="var(--gray-900)"
            />
          </AvatarCol>
          <Content>
            <Header>
              <AuthorGroup>
                <Author>{comment.authorName}</Author>
                {/* Resolve is a thread-level, collaborative action (not
                  owner-only): an icon-only check next to the top-level author's
                  name. Muted + hover-revealed when open; a persistent green
                  check when resolved. Clicking it toggles. */}
                {!isReply && (
                  <ResolveButton
                    type="button"
                    data-testid="comment-resolve"
                    $resolved={isResolved}
                    aria-pressed={isResolved}
                    aria-label={isResolved ? 'Reopen thread' : 'Resolve thread'}
                    data-tooltip-id="comment-resolve-tooltip"
                    data-tooltip-content={
                      isResolved
                        ? 'Resolved · click to reopen'
                        : 'Resolve thread'
                    }
                    onClick={() =>
                      void store.setResolved(comment.id, !isResolved)
                    }
                  >
                    <CircleCheckIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />
                  </ResolveButton>
                )}
              </AuthorGroup>
              <Meta>
                {/* Hover-revealed actions sit BEFORE the timestamp so their
                  reserved (opacity-0) width doesn't push the time out of
                  right alignment with the composer/Post button edge. */}
                {isOwn && !isEditing && (
                  <>
                    <IconButton
                      type="button"
                      data-testid="comment-edit"
                      aria-label="Edit comment"
                      data-tooltip-id="comment-edit-tooltip"
                      data-tooltip-content="Edit comment"
                      onClick={() => startEdit(comment)}
                    >
                      <PencilIcon
                        width="14"
                        height="14"
                        strokeColor="currentColor"
                      />
                    </IconButton>
                    <IconButton
                      type="button"
                      data-testid="comment-delete"
                      aria-label="Delete comment"
                      data-tooltip-id="comment-delete-tooltip"
                      data-tooltip-content="Delete comment"
                      $danger
                      onClick={() =>
                        setPendingDelete({ id: comment.id, isTop: !isReply })
                      }
                    >
                      <DeleteIcon
                        width="14"
                        height="14"
                        strokeColor="currentColor"
                      />
                    </IconButton>
                  </>
                )}
                {!isReply && threadNo != null && (
                  <ThreadNumber data-testid="comment-thread-number">
                    #{threadNo}
                  </ThreadNumber>
                )}
                <Timestamp
                  data-tooltip-id="comment-time-tooltip"
                  data-tooltip-content={timeTooltip}
                >
                  {formatRelativeTimeShort(comment.createdAt)}
                  {comment.editedAt && ' · edited'}
                </Timestamp>
              </Meta>
            </Header>
            {isEditing ? (
              <EditArea>
                {/* Only a top-level comment carries a moment — replies inherit
                    their thread's position, so they get no timecode field. */}
                {!isReply &&
                  (timeDraft === null ? (
                    <PinAction
                      type="button"
                      data-testid="comment-timecode-pin"
                      disabled={isSaving}
                      onClick={() =>
                        setTimeDraft(
                          formatTimecode(
                            playback.seekTime ?? playback.currentTime,
                            framerate,
                          ),
                        )
                      }
                    >
                      Pin to Playhead
                    </PinAction>
                  ) : (
                    <CommentTimecodeField
                      value={timeDraft}
                      onChange={setTimeDraft}
                      framerate={framerate}
                      duration={playback.duration}
                      disabled={isSaving}
                      onUnpin={() => setTimeDraft(null)}
                      onUsePlayhead={() =>
                        setTimeDraft(
                          formatTimecode(
                            playback.seekTime ?? playback.currentTime,
                            framerate,
                          ),
                        )
                      }
                    />
                  ))}
                <EditTextArea
                  ref={editTextareaRef}
                  data-testid="comment-edit-input"
                  value={draft}
                  autoFocus
                  rows={2}
                  onChange={editMention.handleChange}
                  onKeyDown={e => {
                    // The mention popover consumes nav/select/close keys first.
                    if (editMention.handleKeyDown(e)) return;
                    onEditKeyDown(e, comment);
                  }}
                />
                <MentionSuggestions
                  open={editMention.open}
                  results={editMention.results}
                  activeIndex={editMention.activeIndex}
                  setActiveIndex={editMention.setActiveIndex}
                  onSelect={editMention.select}
                  menuRef={editMention.menuRef}
                  menuStyle={editMention.menuStyle}
                  colors={personColors}
                />
                <EditActions>
                  <CancelButton type="button" onClick={cancelEdit}>
                    Cancel
                  </CancelButton>
                  <SaveButton
                    type="button"
                    data-testid="comment-edit-save"
                    disabled={
                      !draft.trim() || isSaving || editedTime() === 'invalid'
                    }
                    onClick={() => void saveEdit(comment)}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </SaveButton>
                </EditActions>
              </EditArea>
            ) : (
              <>
                <Body>
                  {videoTimeSeconds !== null && (
                    <TimecodeChip
                      type="button"
                      data-testid="comment-list-timecode"
                      onClick={() => {
                        if (comment.drawing) {
                          toggleDrawing(comment);
                          return;
                        }
                        seekTo(videoTimeSeconds);
                      }}
                      aria-label="Jump to this moment"
                      data-tooltip-id="comment-jump-tooltip"
                      data-tooltip-content="Jump to this moment"
                    >
                      {convertTimeToMMSS(videoTimeSeconds)}
                    </TimecodeChip>
                  )}
                  {/* PLAT-11293 — marks travel with the comment, so say so in
                      the list. The glyph is the freehand-mark icon: an
                      indicator meaning "carries drawn marks" stays in the same
                      meaning family as the tool that makes them (whereas a
                      pencil would collide with "edit"). The label carries the
                      information for screen readers — never the glyph alone. */}
                  {comment.drawing && (
                    <DrawingIndicator
                      type="button"
                      data-testid="comment-drawing-indicator"
                      $active={selectedDrawingId === comment.id}
                      aria-pressed={selectedDrawingId === comment.id}
                      aria-label={
                        selectedDrawingId === comment.id
                          ? 'Hide this drawing'
                          : 'Show this drawing'
                      }
                      onClick={() => toggleDrawing(comment)}
                      data-tooltip-id="comment-jump-tooltip"
                      data-tooltip-content={
                        selectedDrawingId === comment.id
                          ? 'Hide this drawing'
                          : 'Show this drawing'
                      }
                    >
                      <DrawFreehandIcon
                        width="12"
                        height="12"
                        strokeColor="currentColor"
                      />
                    </DrawingIndicator>
                  )}
                  {splitBodyForRender(comment.body).map((seg, i) =>
                    seg.type === 'mention' ? (
                      <MentionChip key={i} data-testid="comment-mention">
                        @{seg.name}
                      </MentionChip>
                    ) : (
                      <React.Fragment key={i}>{seg.value}</React.Fragment>
                    ),
                  )}
                </Body>
                {reactionEntries.length > 0 && (
                  <ReactionBar>
                    {reactionEntries.map(([emoji, ids]) => {
                      const mine = store.hasReacted(comment, emoji);
                      return (
                        <ReactionPill
                          key={emoji}
                          type="button"
                          $active={mine}
                          aria-pressed={mine}
                          data-testid="comment-reaction-pill"
                          aria-label={`${emoji} ${ids.length}${
                            mine ? ', you reacted' : ''
                          }`}
                          onClick={() =>
                            void store.toggleReaction(comment.id, emoji)
                          }
                        >
                          <span aria-hidden="true">{emoji}</span>
                          <ReactionCount>{ids.length}</ReactionCount>
                        </ReactionPill>
                      );
                    })}
                  </ReactionBar>
                )}
                <Actions>
                  <ReplyLink
                    type="button"
                    data-testid="comment-reply"
                    onClick={() => openReply(topId)}
                  >
                    Reply
                  </ReplyLink>
                  <AddReactionButton
                    type="button"
                    data-testid="comment-add-reaction"
                    aria-label="Add reaction"
                    aria-haspopup="menu"
                    aria-expanded={reactionPickerId === comment.id}
                    data-tooltip-id="comment-add-reaction-tooltip"
                    data-tooltip-content="Add reaction"
                    onClick={e => {
                      reactionTriggerRef.current = e.currentTarget;
                      setReactionPickerId(prev =>
                        prev === comment.id ? null : comment.id,
                      );
                    }}
                  >
                    <EmojiIcon
                      width="15"
                      height="15"
                      strokeColor="currentColor"
                    />
                  </AddReactionButton>
                </Actions>
              </>
            )}
          </Content>
        </Item>
      );
    };

    return (
      <>
        <FilterBar data-testid="comment-filter">
          <ToggleGroup
            options={[
              { id: 'open', label: 'Open', count: statusCounts.open },
              {
                id: 'resolved',
                label: 'Resolved',
                count: statusCounts.resolved,
              },
              { id: 'all', label: 'All', count: statusCounts.all },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            ariaLabel="Filter comments by status"
            testIdPrefix="comment-filter"
          />
          {/* Shell only — Reviewer filter is wired up in a follow-up PR.
              aria-disabled (not native `disabled`) so the "Coming soon" tooltip
              still fires on hover; tabIndex=-1 keeps it out of the tab order. */}
          <StatusTrigger
            type="button"
            $active={false}
            aria-disabled
            tabIndex={-1}
            aria-label="Filter by reviewer (coming soon)"
            data-testid="comment-filter-reviewer"
            data-tooltip-id="comment-filter-shell-tooltip"
            data-tooltip-content="Coming soon"
          >
            <TriggerLabel>Reviewer</TriggerLabel>
            <ChevronDownIcon strokeColor="var(--accent)" />
          </StatusTrigger>
        </FilterBar>
        {/* The status dropdown this used to portal is gone — PLAT-10931
            replaced it with the ToggleGroup in the filter bar above. */}
        {reactionPickerId !== null &&
          reactionMenuStyle &&
          ReactDOM.createPortal(
            <EmojiPickerMenu
              ref={reactionMenuRef}
              style={reactionMenuStyle}
              testId="comment-reaction-picker"
              optionTestId="comment-reaction-option"
              labelFor={emoji => `React ${emoji}`}
              onPick={emoji => {
                const target = reactionPickerId;
                setReactionPickerId(null);
                if (target) void store.toggleReaction(target, emoji);
              }}
            />,
            document.body,
          )}
        <ListViewport>
          <List
            ref={listRef}
            data-testid="comment-list"
            onScroll={syncFadeEdges}
          >
            {filteredThreads.length === 0 && (
              <EmptyState data-testid="comment-filter-empty">
                {filterEmptyMessage}
              </EmptyState>
            )}
            {/* Chat order: oldest at the top, newest at the bottom (nearest the
              composer), matching the store's oldest-first sort. */}
            {filteredThreads.map(({ top, replies, isResolved }) => {
              const replyOpen = replyParentId === top.id;
              // Emphasize the thread while it's the focus of an action: its reply
              // composer open, or a comment within it being edited.
              const isActive =
                replyOpen ||
                editingId === top.id ||
                replies.some(reply => reply.id === editingId);
              return (
                <Thread
                  key={top.id}
                  data-testid="comment-thread"
                  $resolved={isResolved}
                  $active={isActive}
                  $nested={replies.length > 0 || replyOpen}
                >
                  {renderComment(
                    top,
                    top.id,
                    false,
                    isResolved,
                    threadNumber.get(top.id),
                  )}
                  {(replies.length > 0 || replyOpen) && (
                    <Replies data-testid="comment-replies">
                      {replies.map(reply => renderComment(reply, top.id, true))}
                      {replyOpen && (
                        <EditArea data-testid="comment-reply-composer">
                          <EditTextArea
                            ref={replyTextareaRef}
                            data-testid="comment-reply-input"
                            value={replyDraft}
                            autoFocus
                            rows={2}
                            placeholder="Leave a reply…"
                            onChange={replyMention.handleChange}
                            onKeyDown={e => {
                              if (replyMention.handleKeyDown(e)) return;
                              onReplyKeyDown(e, top.id);
                            }}
                          />
                          <MentionSuggestions
                            open={replyMention.open}
                            results={replyMention.results}
                            activeIndex={replyMention.activeIndex}
                            setActiveIndex={replyMention.setActiveIndex}
                            onSelect={replyMention.select}
                            menuRef={replyMention.menuRef}
                            menuStyle={replyMention.menuStyle}
                            colors={personColors}
                          />
                          <EditActions>
                            <CancelButton type="button" onClick={cancelReply}>
                              Cancel
                            </CancelButton>
                            <SaveButton
                              type="button"
                              data-testid="comment-reply-post"
                              disabled={!replyDraft.trim() || replyPosting}
                              onClick={() => void postReply(top.id)}
                            >
                              {replyPosting ? 'Posting…' : 'Reply'}
                            </SaveButton>
                          </EditActions>
                        </EditArea>
                      )}
                    </Replies>
                  )}
                </Thread>
              );
            })}
            {ReactDOM.createPortal(
              <>
                <Tooltip
                  id="comment-resolve-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="bottom"
                  positionStrategy="fixed"
                />
                <Tooltip
                  id="comment-edit-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="bottom"
                  positionStrategy="fixed"
                />
                <Tooltip
                  id="comment-delete-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="bottom"
                  positionStrategy="fixed"
                />
                <Tooltip
                  id="comment-jump-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="top"
                  positionStrategy="fixed"
                />
                <Tooltip
                  id="comment-time-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="top"
                  positionStrategy="fixed"
                />
                <Tooltip
                  id="comment-filter-shell-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="bottom"
                  positionStrategy="fixed"
                />
                <Tooltip
                  id="comment-add-reaction-tooltip"
                  style={COMPACT_TOOLTIP_STYLE}
                  place="top"
                  positionStrategy="fixed"
                />
              </>,
              document.body,
            )}
          </List>
          <TopFade $visible={fadeEdges.hasAbove} />
          <BottomFade $visible={fadeEdges.hasBelow} />
        </ListViewport>
        {pendingDelete && (
          <ConfirmModal
            title="Delete this comment?"
            message={
              pendingDelete.isTop
                ? 'Its replies will be deleted too. This cannot be undone.'
                : 'This cannot be undone.'
            }
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={confirmDelete}
            onCancel={() => setPendingDelete(null)}
            closeModal={() => setPendingDelete(null)}
          />
        )}
      </>
    );
  },
);

// Status-filter row above the thread list (holds the Status dropdown trigger).
// Sits between the composer and the scrolling list (both siblings inside
// CommentsContainer); left-aligned and compact.
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 6px);
  flex-shrink: 0;
`;

// Compact bordered pill trigger + gold caret, mirroring the homepage
// Type / Clip Status filters (docs/design-system.md "Form Fields"): quiet border
// at rest, accent border + faint accent fill once a status is selected (accent =
// selected, so hover doesn't escalate then).
const StatusTrigger = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-primary);
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid
    ${props => (props.$active ? 'var(--accent)' : 'var(--border)')};
  background-color: ${props =>
    props.$active
      ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
      : 'transparent'};
  color: ${props =>
    props.$active ? 'var(--accent)' : 'var(--text-secondary)'};
  transition: border-color var(--transition-fast);

  &:hover:not([aria-disabled='true']) {
    border-color: ${props =>
      props.$active ? 'var(--accent)' : 'var(--text-muted)'};
  }

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    flex-shrink: 0;
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Positioning anchor for the edge fades: the scrolling List fills it and the
// Top/Bottom gradient overlays pin to its edges (so they don't scroll away).
const ListViewport = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  /* Cards are subtle/transparent at rest and highlight on hover; the card
     padding supplies most of the inter-thread separation, so the gap stays
     minimal. */
  gap: 2px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Room below the newest comment so the bottom fade softens whitespace, not
     the comment's text, when scrolled to the bottom. */
  padding-bottom: var(--spacing-md, 16px);
  /* A right gutter so the scrollbar gets its own lane and doesn't draw over the
     comment cards. */
  padding-right: var(--spacing-sm, 8px);
`;

// Edge fades that soften the seam where the scrolling list meets the composer
// (bottom) and the header (top) — same gradient idiom as ItemDropdownSelect's
// EdgeFade, fading to the panel's --background. Spans the full width;
// non-interactive.
const TopFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 16px;
  pointer-events: none;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity var(--transition-fast, 0.15s) ease;
  background: linear-gradient(to bottom, var(--background), transparent);
`;

const BottomFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  pointer-events: none;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity var(--transition-fast, 0.15s) ease;
  /* Solid hold near the edge so it visibly softens the seam on the dark theme. */
  background: linear-gradient(
    to top,
    var(--background) 0%,
    var(--background) 20%,
    transparent 100%
  );
`;

// A top-level comment plus its replies/composer. When resolved, the thread
// stays in the list (so handled feedback is still visible) but its body text is
// dimmed to read as "done"; the green Resolved badge in the header is left at
// full strength as the indicator.
const Thread = styled.div<{
  $resolved?: boolean;
  $active?: boolean;
  $nested?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs, 6px);
  /* Subtle by default (transparent), Frame.io-style. The active thread — hovered,
     or with its reply/edit composer open — gets a soft "destaque": a --surface
     fill only (no border), with rounded corners. Fill-only means no box-size
     change, so the highlight never shifts the row. Padding kept lean and
     near-balanced (6px top / 8px sides) — a big top-vs-left mismatch makes the
     fill read lopsided, and the vertical padding (plus the tiny List gap) is
     the whole inter-thread separation. */
  padding: 6px 8px;
  border-radius: var(--radius, 8px);
  transition: background var(--transition-fast, 0.15s) ease;

  ${props =>
    props.$active &&
    css`
      background: var(--surface);
    `}

  &:hover {
    background: var(--surface);
  }

  ${props =>
    props.$resolved &&
    css`
      ${Body} {
        opacity: 0.6;
      }
    `}

  /* The thread rail (PLAT-12039): one 2px line from under the top comment's
     avatar to the bottom of the nest (the last reply, or the open reply
     composer), so the parent's body, its reply row and every reply hang off
     the same avatar. It lives on the Thread, not the replies block, because
     only the Thread spans the parent's rows: a rail that starts at the replies
     block stops short, below the parent's text, and reads as a stray stroke.
     Drawn only when there is something nested ($nested).

     Line Diet: this is the thread's one line, and it is what says "these hang
     off that comment" — the indent alone reads as a stray margin when a thread
     has a single reply. --border-subtle, integer-aligned: the avatar sits at
     x = 8px (Thread padding) and its centre at +10, so the stroke spans
     17–19px; no translateX(-50%), which would antialias into a blur. It starts
     at the avatar's centre (6px padding + 10px) and the opaque avatar, lifted
     by AvatarCol's z-index, covers that end so the rail appears to emerge from
     under it. It ends at the Thread's bottom padding. */
  ${props =>
    props.$nested &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: 17px;
        top: 16px;
        bottom: 6px;
        width: 2px;
        background: var(--border-subtle, var(--border));
      }
    `}
`;

// Replies (and the reply composer) nested one level under the top comment
// (PLAT-12039). Indented by one avatar column — the 20px avatar plus the Item's
// 8px gap — so a reply's avatar starts where the parent's text starts, to the
// right of the Thread rail. Frame.io's flat layout (every avatar in one column,
// a short connector between them) was tried in PLAT-10893 and rejected: a
// reply there is hard to tell from a new comment.
const REPLY_INDENT = 28;
const Replies = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs, 6px);
  margin-left: ${REPLY_INDENT}px;
`;

// A single comment: avatar in a fixed left column, the content column (header +
// body + actions) to its right. `align-items: flex-start` tops the avatar level
// with the author row.
const Item = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--spacing-sm, 8px);
`;

// Fixed avatar column; lifted above the thread rail so the opaque parent
// avatar covers the rail's top end.
const AvatarCol = styled.div`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
`;

// The comment's text column beside the avatar. `min-width: 0` lets the body wrap
// and the author name truncate instead of overflowing the card.
const Content = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: var(--spacing-2xs, 4px);
`;

// Absolute thread number ("#3"), muted and quiet in the header meta row.
const ThreadNumber = styled.span`
  font-size: var(--text-xs, 12px);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

// The name/meta row. Pinned to the avatar's height (20px) and center-aligned so
// the avatar (top-aligned in its column) lines up vertically with the name.
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm, 8px);
  min-height: 20px;
`;

// Author name + the resolve toggle, grouped on the left of the header so the
// resolved ✓ reads as belonging to the thread's author.
const AuthorGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 6px);
  min-width: 0;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 6px);
`;

// Owner-only edit/delete affordances; hidden until the comment is hovered or
// the button is focused, so they stay out of the way but are keyboard-reachable.
// `$danger` (delete) brightens to the destructive color on hover.
const IconButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--transition-fast, 0.15s) ease;

  ${Item}:hover &,
  &:focus-visible {
    opacity: 1;
  }

  &:hover {
    color: ${props =>
      props.$danger ? 'var(--destructive)' : 'var(--foreground)'};
  }
`;

// Thread-level resolve toggle — an icon-only check (no text label), sharp and
// consistent with the edit/delete icon buttons. Unresolved: muted + hover-
// revealed (like the edit affordance), tinting green on hover to preview the
// action. Resolved: a persistent green check marking the thread as handled;
// click reopens it.
const ResolveButton = styled.button<{ $resolved: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${props => (props.$resolved ? 'var(--primary)' : 'var(--text-muted)')};
  opacity: ${props => (props.$resolved ? 1 : 0)};
  transition:
    opacity var(--transition-fast, 0.15s) ease,
    color var(--transition-fast, 0.15s) ease;

  ${Item}:hover &,
  &:focus-visible {
    opacity: 1;
  }

  &:hover {
    color: ${props =>
      props.$resolved ? 'var(--primary-hover)' : 'var(--primary)'};
  }
`;

// Author name in the design-system primary text color (--foreground), not the
// speaker color — the colored avatar carries the per-person hue. Brighter than
// the --text-secondary body, so the name reads as the label above its content.
const Author = styled.span`
  font-size: var(--text-xs, 12px);
  font-weight: var(--font-weight-medium, 500);
  color: var(--foreground);
`;

const Timestamp = styled.span`
  font-size: var(--text-xs, 12px);
  color: var(--text-muted);
`;

const Body = styled.p`
  margin: 0;
  font-size: var(--text-sm, 14px);
  line-height: 1.6;
  /* Matches the transcript word color (--text-secondary). */
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
`;

// An @-mention rendered inline in the body — brighter than the surrounding
// secondary text so it reads as a reference to a person.
// A rendered @mention — gold accent at regular weight, one size down from the
// body (12px vs 14px) so it reads as a reference without shouting.
const MentionChip = styled.span`
  color: var(--accent);
  font-size: var(--text-xs, 12px);
`;

// Action row under a comment's body: Reply, plus the Resolve toggle on
// top-level comments. Centers the items so the resolve icon lines up with text.
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md, 16px);
  /* No extra margin — the Content column's own gap already separates the
     hover-revealed Reply row, keeping the reserved (opacity-hidden) row as
     short as possible. */
`;

// Quiet "Reply" link under a comment; hidden at rest and revealed on hover/focus
// (like the edit + resolve affordances) so a comment reads as just author, time,
// and body until you engage with it. Brightens neutrally on hover (gold is
// reserved for visual controls, not actions).
const ReplyLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  font-size: var(--text-xs, 12px);
  font-weight: var(--font-weight-medium, 500);
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--transition-fast, 0.15s) ease;

  ${Item}:hover &,
  &:focus-visible {
    opacity: 1;
  }

  &:hover {
    color: var(--foreground);
  }
`;

// "Add reaction" affordance in the action row — a hover-revealed emoji-face icon
// (like Reply). Stays visible while its picker is open (aria-expanded).
const AddReactionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  opacity: 0;
  transition:
    opacity var(--transition-fast, 0.15s) ease,
    color var(--transition-fast, 0.15s) ease;

  ${Item}:hover &,
  &:focus-visible,
  &[aria-expanded='true'] {
    opacity: 1;
  }

  &:hover {
    color: var(--foreground);
  }
`;

// Row of reaction pills below a comment's body.
const ReactionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2xs, 4px);
  margin-top: var(--spacing-2xs, 4px);
`;

// One reaction (emoji + count). "Mine" reuses the active-filter-pill treatment
// (accent border + faint accent fill); others sit on --surface-elevated. No
// gold fill beyond the subtle selected tint — accent marks "you reacted".
const ReactionPill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 var(--spacing-xs, 6px);
  border-radius: 999px;
  font-size: var(--text-xs, 12px);
  line-height: 1;
  cursor: pointer;
  border: 1px solid
    ${props => (props.$active ? 'var(--accent)' : 'var(--border)')};
  background: ${props =>
    props.$active
      ? 'color-mix(in srgb, var(--accent) 14%, transparent)'
      : 'var(--surface-elevated)'};
  color: ${props =>
    props.$active ? 'var(--accent)' : 'var(--text-secondary)'};
  transition:
    border-color var(--transition-fast, 0.15s) ease,
    background var(--transition-fast, 0.15s) ease;

  &:hover {
    border-color: ${props =>
      props.$active ? 'var(--accent)' : 'var(--text-muted)'};
  }
`;

const ReactionCount = styled.span`
  font-variant-numeric: tabular-nums;
`;

// Inline editor / reply composer — mirrors the composer's textarea + primary
// action styling.
const EditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 8px);
`;

const EditTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  padding: var(--spacing-sm, 8px);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 6px);
  /* Subtle gray resting fill, matching the composer + shared input primitives. */
  background: var(--input-background);
  color: var(--foreground);
  font-family: var(--font-primary, Inter, sans-serif);
  font-size: var(--text-sm, 14px);
  line-height: 1.5;
  transition: border-color var(--transition-fast, 0.15s);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const EditActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-sm, 8px);
`;

/**
 * Attaches a moment to a general comment, standing in for the timecode field
 * until there is one. Text-weight so it reads as an option rather than a step
 * you're expected to take — most general comments are meant to stay general.
 */
const PinAction = styled.button`
  align-self: flex-start;
  margin-bottom: 6px;
  padding: 0 2px;
  border: none;
  background: none;
  color: var(--text-muted);
  font-size: var(--text-xs, 12px);
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--foreground);
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CancelButton = styled.button`
  height: var(--btn-h-sm, 32px);
  padding: 0 var(--btn-px-sm, 12px);
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: none;
  color: var(--text-secondary);
  font-size: var(--text-xs, 12px);
  cursor: pointer;

  &:hover {
    color: var(--foreground);
  }
`;

const SaveButton = styled.button`
  height: var(--btn-h-sm, 32px);
  padding: 0 var(--btn-px-sm, 12px);
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: var(--primary);
  color: var(--background);
  font-size: var(--text-xs, 12px);
  font-weight: var(--font-weight-semi-bold, 600);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Timecode as a restrained chip (shared look with the composer's timecode
// toggle): a --surface-elevated pill on the card, muted text, tabular numerals.
// Brightens on hover to read as a clickable seek control. No gold — gold is
// reserved for visual controls per the design system.
// Sits between the timecode chip and the body text; sized to the text line so
// it never changes the row height (PLAT-11293 acceptance: layout unchanged).
const DrawingIndicator = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-right: var(--spacing-sm, 8px);
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  /* Gold while its marks are on screen — the design system's selected state;
     green is for actions and is never a selection indicator. */
  color: ${props => (props.$active ? 'var(--accent)' : 'var(--text-muted)')};

  &:hover {
    color: var(--foreground);
  }
`;

const TimecodeChip = styled.button`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-right: var(--spacing-sm, 8px);
  padding: 1px var(--spacing-xs, 6px);
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: var(--surface-elevated);
  color: var(--text-secondary);
  font-size: var(--text-xs, 12px);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: color var(--transition-fast, 0.15s) ease;

  &:hover {
    color: var(--foreground);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: var(--text-muted);
  font-size: var(--text-sm, 14px);
  text-align: center;
  line-height: 1.5;
`;

// Illustration + message, stacked and centred. Capped narrower than the Story
// Manager details panel's 200px: this tab is taller and otherwise empty, so at
// 200px the artwork dominates instead of introducing the message.
const EmptyArt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  max-width: 140px;
`;
